import { Component, computed, DestroyRef, inject, signal, ChangeDetectionStrategy } from "@angular/core";
import { takeUntilDestroyed, toObservable, toSignal } from "@angular/core/rxjs-interop";
import { Router } from "@angular/router";
import { CampaignType, SbCreativeType, StrategyAsin } from "@front/m19-api-client";
import { SbStrategyEx } from "@front/m19-models/strategy/SbStrategyEx";
import { AccountSelectionService, BrandAssetsService, Constant, MapPipe } from "@front/m19-services";
import { IButtonDirective, IIconComponent, ISelectMenuImports, ITooltipDirective, Option } from "@front/m19-ui";
import { TranslocoDirective, TranslocoService } from "@jsverse/transloco";
import { LowInventoryWarningComponent } from "@m19-board/inventory/inventory-rules/low-inventory-warning.component";
import { SB_CREATIVE_TYPE_LABEL_KEYS, SbCreativeCardComponent } from "@m19-board/shared/sb-creative-card.component";
import { StrategyPageService } from "@m19-board/strategies/strategy-page/strategy-page.service";
import { filter, map, switchMap } from "rxjs/operators";

const SB_STRATEGIES_URL = "/advertising/sponsored-brands";

@Component({
  selector: "app-strategy-page-sb-creatives",
  imports: [
    IButtonDirective,
    IIconComponent,
    ITooltipDirective,
    LowInventoryWarningComponent,
    TranslocoDirective,
    MapPipe,
    ISelectMenuImports,
    SbCreativeCardComponent,
  ],
  templateUrl: "./strategy-page-sb-creatives.component.html",
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class StrategyPageSbCreativesComponent {
  private readonly strategyPageService = inject<StrategyPageService<CampaignType.SB>>(StrategyPageService);
  private readonly translocoService = inject(TranslocoService);
  private readonly router = inject(Router);
  private readonly accountSelectionService = inject(AccountSelectionService);
  private readonly brandAssetsService = inject(BrandAssetsService);
  private readonly destroyRef = inject(DestroyRef);

  readonly maxCreativesPerStrategy = Constant.maxCreativesPerStrategy;
  readonly creativeTypeLabelKeys = SB_CREATIVE_TYPE_LABEL_KEYS;
  readonly creativeTypesOptions = Object.values(SbCreativeType).map((type) => ({
    label: this.translocoService.translate(this.creativeTypeLabelKeys[type]),
    value: type,
  }));

  strategy = this.strategyPageService.strategy;

  readonly creatives = toSignal(
    toObservable(this.strategy).pipe(
      takeUntilDestroyed(this.destroyRef),
      filter((s): s is SbStrategyEx => !!s),
      switchMap((s) =>
        this.brandAssetsService
          .getSbCreativesPerStrategy(s.accountId, s.marketplace)
          .pipe(map((creatives) => creatives.get(s.strategyId) ?? [])),
      ),
    ),
  );

  isReadOnly = toSignal(this.accountSelectionService.readOnlyMode$);

  readonly selectedCreativeTypes = signal<Option<SbCreativeType>[]>([]);
  readonly filteredCreatives = computed(() => {
    if (!this.selectedCreativeTypes().length) return this.creatives() ?? [];
    return this.creatives()?.filter((c) => this.selectedCreativeTypes().some((v) => v.value === c.creativeType)) ?? [];
  });

  readonly canCreateNewCreative = computed(() => {
    return (this.creatives()?.length ?? 0) < Constant.maxCreativesPerStrategy;
  });

  readonly creativeLimitSoonReached = computed(() => {
    return (
      (this.creatives()?.length ?? 0) >= Constant.maxCreativesPerStrategy * 0.8 &&
      (this.creatives()?.length ?? 0) < Constant.maxCreativesPerStrategy
    );
  });

  getAsins(strategyAsin: StrategyAsin): string {
    return strategyAsin.asin;
  }

  newCreative() {
    if (!this.canCreateNewCreative()) return;
    const strategyId = this.strategy()?.strategyId;
    if (!strategyId) return;
    void this.router.navigate([SB_STRATEGIES_URL, strategyId, "new-ad-line"]);
  }
}
